import { marked } from 'marked';

// Eagerly import all markdown files as raw strings at build time
const rawFiles = import.meta.glob<string>('/src/content/blog/*.md', {
    query: '?raw',
    import: 'default',
    eager: true,
});

export interface PostMeta {
    slug: string;
    title: string;
    excerpt: string;
    category: string;
    date: string;
}

export interface Post extends PostMeta {
    html: string;
}

function parseFrontmatter(raw: string): {
    data: Record<string, string>;
    content: string;
} {
    const match = raw.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n([\s\S]*)$/);
    if (!match) return { data: {}, content: raw };

    const data: Record<string, string> = {};
    for (const line of match[1].split('\n')) {
        const colonIdx = line.indexOf(':');
        if (colonIdx === -1) continue;
        data[line.slice(0, colonIdx).trim()] = line.slice(colonIdx + 1).trim();
    }

    return { data, content: match[2].trim() };
}

function formatDate(dateStr: string): string {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

function parseFile(raw: string): { meta: PostMeta; content: string } {
    const { data, content } = parseFrontmatter(raw);
    return {
        meta: {
            slug: data.slug ?? '',
            title: data.title ?? '',
            excerpt: data.excerpt ?? '',
            category: data.category ?? '',
            date: data.date ? formatDate(data.date) : '',
        },
        content,
    };
}

export function getAllPosts(): PostMeta[] {
    return Object.values(rawFiles)
        .map((raw) => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { data, content: _ } = parseFrontmatter(raw);
            return { rawDate: data.date ?? '', meta: parseFile(raw).meta };
        })
        .filter(({ meta }) => meta.slug)
        .sort((a, b) => b.rawDate.localeCompare(a.rawDate))
        .map(({ meta }) => meta);
}

export function getPost(slug: string): Post | undefined {
    for (const raw of Object.values(rawFiles)) {
        const { meta, content } = parseFile(raw);
        if (meta.slug === slug) {
            return { ...meta, html: marked(content) as string };
        }
    }
    return undefined;
}
